import {
  DataFetcher,
  GrpcResponseError,
  useLegacyGrpcRequest,
} from '@aboutyou/data-fetcher';
import {CheckoutV1_getCheckoutDetailsLazy} from '@aboutyou/grpc-clients/grips/protos/checkout/checkout_v1.grpc.lazy';
import {useHydrationDone} from '@aboutyou/lazy-imports';
import {Log, Timing} from '@aboutyou/mlm';
import {useBasketRoute} from '@aboutyou/router';
import {
  ErrorPageInline,
  Flyout,
  HEADER_HEIGHT,
  Z_INDEXES,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useCallback} from 'react';
import {useRenderAsTwa} from '../../client-services/Twa/hooks/useRenderAsTwa';
import {getAppIntentUrlForDevices} from '../../features/AppRedirect/helpers';
import {CheckoutWebComponent} from './CheckoutWebComponent/CheckoutWebComponent';
import {
  CheckoutHashDetails,
  useCheckoutHashDetails,
} from './app-checkout/CheckoutHashDetailsProvider';
import {CheckoutAnimation} from './components/CheckoutAnimation/CheckoutAnimation';
import {ForceAppRedirectListener} from './components/ForceAppRedirectListener';
import {useAppUrlParams} from './hooks/useAppUrlParams';
import {useAppCheckoutEntryDetails} from './hooks/useCheckoutEntryDetails';
import {useGetAccessTokenCallback} from './hooks/useGetAccessTokenCallback';
import {dispatchCheckoutTrackingEvent} from './tracking/CheckoutTrackingListener';

export const AppCheckout = () => {
  const timing = new Timing();
  const {oAuthHash} = useAppUrlParams();
  const isTwaEnabled = useRenderAsTwa();

  const checkoutHashDetails = useCheckoutHashDetails();

  if (checkoutHashDetails) {
    return (
      <>
        <ForceAppRedirectListener />

        {!isTwaEnabled && <NonClickableTransparentHeaderCover />}

        <AppCheckoutView
          accessToken={checkoutHashDetails.accessToken}
          authToken={checkoutHashDetails.authToken}
          jwt={checkoutHashDetails.jwt}
          origin={checkoutHashDetails.origin}
          shopId={checkoutHashDetails.shopId}
          authProvider={checkoutHashDetails.authProvider}
          timing={timing}
        />
      </>
    );
  }

  return (
    <>
      <ForceAppRedirectListener />

      {!isTwaEnabled && <NonClickableTransparentHeaderCover />}

      <CheckoutAccessTokenFetcher oAuthHash={oAuthHash!} />
    </>
  );
};

/**
 * Leads to review page
 */
export const AppExpressCheckout = () => {
  const hydrationDone = useHydrationDone();

  const {
    entryTimeStamp,
    clientEntryTimeStamp,
    transactionId,
    authToken,
    authProvider,
    showSecondaryPriceLabel,
  } = useAppUrlParams();
  const isTwaEnabled = useRenderAsTwa();
  const timing = new Timing();

  const onAccessTokenRefreshFailure = useHandleAccessTokenRefreshFailure();
  const getOnInvalidAccessToken = useHandleInvalidAccessToken(
    onAccessTokenRefreshFailure,
  );

  if (!hydrationDone) {
    return <CheckoutAnimation.Show />;
  }

  return (
    <>
      <ForceAppRedirectListener />

      {!isTwaEnabled && <NonClickableTransparentHeaderCover />}

      <CheckoutWebComponent
        transactionId={transactionId!}
        origin={'app'}
        timing={timing}
        entryTimeStamp={entryTimeStamp}
        clientEntryTimeStamp={clientEntryTimeStamp}
        onInvalidAccessToken={getOnInvalidAccessToken({
          authToken,
          authProvider: `${authProvider}-express`,
        })}
        onAccessTokenRefreshFailure={onAccessTokenRefreshFailure}
        showSecondaryPriceLabel={showSecondaryPriceLabel}
        hasDatadogConsent={false}
      />
    </>
  );
};

const CheckoutAccessTokenFetcher: React.FC<{
  oAuthHash: string;
}> = ({oAuthHash}) => {
  const timing = new Timing();
  const basketRoute = useBasketRoute();

  const request = useLegacyGrpcRequest(CheckoutV1_getCheckoutDetailsLazy, {
    hash: oAuthHash,
  });

  return (
    <DataFetcher
      request={request}
      View={AppCheckoutView}
      viewProps={{timing}}
      Error={({error}) => {
        Log.error(
          '@aboutyou/logic-components',
          'CheckoutAccessTokenFetcher Error',
          {error: error as Error},
        );

        dispatchCheckoutTrackingEvent({
          event: 'error',
          label: (error as Error).message,
          action: 'CheckoutAccessTokenFetcher',
          statusCode: (error as GrpcResponseError)?.code?.toString(),
        });

        CheckoutAnimation.End();

        return (
          <ErrorViewWrapper>
            <ErrorPageInline url={basketRoute} />
          </ErrorViewWrapper>
        );
      }}
      Loading={CheckoutAnimation.Show}
    />
  );
};

const AppCheckoutView: React.FC<
  CheckoutHashDetails & {timing: Timing}
> = props => {
  const {
    correlationId,
    entryTimeStamp,
    clientEntryTimeStamp,
    showSecondaryPriceLabel,
  } = useAppUrlParams();

  const onAccessTokenRefreshFailure = useHandleAccessTokenRefreshFailure();
  const getOnInvalidAccessToken = useHandleInvalidAccessToken(
    onAccessTokenRefreshFailure,
  );

  const checkoutEntryDetails = useAppCheckoutEntryDetails();
  const experiments = checkoutEntryDetails.jwtPayload?.experiments ?? [];
  const appClientVersion =
    checkoutEntryDetails.appConfig?.app?.app_version ?? '0.0.0'; // old version of the app might not send the appCOnfig, so we default to 0.0.0 in that case

  return (
    <CheckoutWebComponent
      timing={props.timing}
      accessToken={props.accessToken}
      authToken={props.authToken}
      jwt={props.jwt}
      origin={props.origin}
      SustainabilityInfoLayoutComponent={Flyout}
      BnplInfoLayoutComponent={Flyout}
      ProductDetailsLayoutComponent={Flyout}
      correlationId={correlationId!}
      entryTimeStamp={entryTimeStamp}
      clientEntryTimeStamp={clientEntryTimeStamp}
      onInvalidAccessToken={getOnInvalidAccessToken({
        authToken: props.authToken,
        authProvider: props.authProvider,
      })}
      onAccessTokenRefreshFailure={onAccessTokenRefreshFailure}
      showSecondaryPriceLabel={showSecondaryPriceLabel}
      hasDatadogConsent={false}
      experiments={experiments}
      appClientVersion={appClientVersion}
    />
  );
};

const useHandleInvalidAccessToken = (
  onAccessTokenRefreshError: VoidFunction,
) => {
  const getAccessToken = useGetAccessTokenCallback();

  return useCallback(
    ({
        authToken,
        authProvider,
      }: {
        authToken: string | null;
        authProvider: string | null;
      }) =>
      () => {
        return getAccessToken({
          authToken,
          authProvider,
          onAccessTokenRefreshError,
        });
      },
    [onAccessTokenRefreshError],
  );
};

const useHandleAccessTokenRefreshFailure = () => {
  const {device} = useAppUrlParams();

  return useCallback(() => {
    const redirectUrl = getAppIntentUrlForDevices({
      device,
      searchParams: '',
      intent: 'basket',
    });

    window.location.href = redirectUrl;
  }, [device]);
};

/**
 * Prevents users from interacting ( clicking, long-pressing ) on the Logo
 * when Checkout page is shown in the App
 */
const NonClickableTransparentHeaderCover = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: ${HEADER_HEIGHT}px;
  width: 100vw;
  z-index: ${Z_INDEXES.MAIN_HEADER + 1};
`;

const ErrorViewWrapper = styled.div`
  margin-top: 4rem;
`;
